import React, { useState } from 'react'
import { SxProps } from '@mui/material'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'

import ContentBox from 'components_new/molecules/ContentBox'
import ContentfulRichTextRendered from 'components_new/molecules/ContentfulRichTextRenderer'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import FormSection from 'components_new/molecules/FormSection'
import NavigationItem from 'components_new/molecules/NavigationItem'
import SimpleTable from 'components_new/molecules/SimpleTable'

import ConfigureSlicesDialog, {
  TConfigureSlice
} from 'components_new/organisms/dialogs/ConfigureSlicesDialog'

import Loading from './loading'

import { translateMetricStatus } from 'utils/enumTranslator'
import { useParams } from 'utils/hooks'

import { Context } from 'types/GlobalUserEvents'

import { useMetricActions } from 'redux/actions/Metrics'
import { useMetric } from 'redux/hooks/Metrics'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

import { addRef, useQueryParamState } from 'utils/queryHandler'

import { isCustomEconomyMetric, isStandardMetric } from 'types/GlobalMetric'

import { useTranslation } from 'language/translator'

interface Props {
  sx?: SxProps
}

const MetricAbout = React.memo((props: Props) => {
  const { sx } = props
  const { id } = useParams<{ id: string }>()
  const { isAdmin } = useAccountSettings()
  const { t, translateEnum, lang } = useTranslation()
  const [variant] = useQueryParamState<string | null>('variant', null)
  const metric = useMetric(id, variant)
  const { tryGetAllMetrics } = useMetricActions()

  const [configureSlice, setConfigureSlice] = useState<TConfigureSlice | null>(
    null
  )

  const handleCloseConfigureSlice = (changed: boolean) => {
    if (changed) {
      tryGetAllMetrics()
    }

    setConfigureSlice(null)
  }

  if (!metric) {
    return <Loading />
  }

  // success
  return (
    <Box sx={{ ...sx }}>
      {isCustomEconomyMetric(metric) ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('metrics', 'custom_economy_metric_info')}
        </Alert>
      ) : null}
      <ContentBox
        collapse={{
          active: true,
          defaultCollapsed: false,
          unmountOnExit: true
        }}
        color="default"
        gutterBottom={true}
        header={{
          icon: { name: 'Info' },
          title: t('global', 'about')
        }}
      >
        <SimpleTable
          rows={[
            { cells: [t('global', 'unit'), metric.unit] },
            {
              cells: [
                t('global', 'description'),
                isStandardMetric(metric) ? metric.summary[lang] : ''
              ]
            },
            {
              cells: [
                t('global', 'category'),
                <>
                  <Chip label={metric.category.title[lang]} size="xsmall" />
                  {' / '}
                  <Chip label={metric.subcategory.title[lang]} size="xsmall" />
                </>
              ]
            },
            {
              cells: [
                t('entities', 'entity'),
                <>
                  <Chip
                    label={metric.entity?.title[lang] || t('global', 'missing')}
                    size="xsmall"
                    href={
                      isAdmin && metric.entity
                        ? `/entities/${metric.entity.id}`
                        : undefined
                    }
                    search={
                      metric.entity
                        ? addRef('', Context.FROM_METRIC)
                        : undefined
                    }
                  />
                </>
              ]
            },
            {
              cells: [
                t('global', 'status'),
                <Chip
                  color={translateMetricStatus[metric.status].color}
                  key={metric.id}
                  label={translateEnum('metric_statuses', metric.status).title}
                  size="xsmall"
                />
              ]
            }
          ]}
          sx={{ mb: 2 }}
        />

        {isStandardMetric(metric) ? (
          <ContentfulRichTextRendered
            value={metric.body[lang]}
            variant="body2"
          />
        ) : null}

        {isStandardMetric(metric) ? (
          <Box sx={{ pt: 2 }}>
            <NavigationItem
              external={true}
              item={{
                href: metric.url,
                title: t('global', 'read_more_on_homepal_se')
              }}
            />
          </Box>
        ) : null}
      </ContentBox>
      {isStandardMetric(metric) ? (
        <ContentBox
          collapse={{
            active: true,
            defaultCollapsed: true,
            unmountOnExit: true
          }}
          color="default"
          gutterBottom={true}
          header={{
            icon: { name: 'Calculation' },
            title: t('metrics', 'calculation')
          }}
        >
          <ContentfulRichTextRendered value={metric.calculation[lang]} />
          {metric.definition_slices.length > 0 ? (
            <Box sx={{ pt: 2 }}>
              <FormSection
                helperText={t('metrics', 'definition_slices_helper')}
                title={t('metrics', 'definition_slices_title')}
              >
                {metric.definition_slices.map((slice) => (
                  <NavigationItem
                    arrow={isAdmin}
                    item={{
                      chip: slice.configured
                        ? undefined
                        : {
                            color: 'warning',
                            icon: <Icon name="Error" />,
                            label: t('entities', 'not_configured'),
                            size: 'xsmall'
                          },
                      icon: { name: 'Slice' },
                      onClick: isAdmin
                        ? () => setConfigureSlice(slice)
                        : undefined,
                      title: slice.name
                    }}
                    key={slice.id}
                  />
                ))}
              </FormSection>
            </Box>
          ) : null}
        </ContentBox>
      ) : null}
      <ContentBox
        collapse={{
          active: true,
          defaultCollapsed: true,
          unmountOnExit: true
        }}
        color="default"
        gutterBottom={true}
        header={{
          icon: { name: 'Slices' },
          title: t('entities', 'slices'),
          titleAdornment: <Chip label={metric.slices.all.length} size="small" />
        }}
      >
        {metric.slices.all.length > 0 ? (
          metric.slices.all.map((slice) => (
            <NavigationItem
              arrow={isAdmin}
              item={{
                chip: slice.disabled
                  ? {
                      label: t('entities', 'not_configured'),
                      color: 'warning',
                      size: 'xsmall',
                      icon: <Icon name="Error" />
                    }
                  : undefined,
                icon: { name: 'Slice' },
                body: metric.entity?.title[lang],
                onClick:
                  isAdmin && slice.entity_id
                    ? () =>
                        setConfigureSlice({
                          configured: !slice.disabled,
                          entity_id: slice.entity_id,
                          id: slice.id
                        })
                    : undefined,
                title: slice.name
              }}
              key={slice.id}
            />
          ))
        ) : (
          <EmptyPlaceholder
            background={true}
            title={t('entities', 'no_slices_yet')}
          />
        )}
      </ContentBox>
      <ContentBox
        collapse={{
          active: true,
          defaultCollapsed: true,
          unmountOnExit: true
        }}
        color="default"
        gutterBottom={true}
        header={{
          icon: { name: 'RollingSum' },
          title: t('metrics', 'rolling_sums'),
          titleAdornment: (
            <Chip label={metric.rolling_sums.length} size="small" />
          )
        }}
      >
        {metric.rolling_sums.length > 0 ? (
          <SimpleTable
            rows={metric.rolling_sums.map((rsum) => ({
              cells: [rsum, translateEnum('rolling_types', rsum).title]
            }))}
          />
        ) : (
          <EmptyPlaceholder
            background={true}
            title={t('metrics', 'rolling_sums_empty_title')}
          />
        )}
      </ContentBox>
      <ContentBox
        collapse={{
          active: true,
          defaultCollapsed: true,
          unmountOnExit: true
        }}
        color="default"
        gutterBottom={true}
        header={{
          icon: { name: 'Distribution' },
          title: t('metrics', 'distributions'),
          titleAdornment: (
            <Chip label={metric.distributions.length} size="small" />
          )
        }}
      >
        {metric.distributions.length > 0 ? (
          <SimpleTable
            rows={metric.distributions.map((dist) => ({
              cells: [
                translateEnum('show_pers', dist.enum).abbreviation,
                translateEnum('show_pers', dist.enum).title
              ]
            }))}
          />
        ) : (
          <EmptyPlaceholder
            background={true}
            title={t('metrics', 'no_comparison_variants_yet')}
          />
        )}
      </ContentBox>

      <ConfigureSlicesDialog
        onClose={handleCloseConfigureSlice}
        open={configureSlice !== null}
        slices={configureSlice ? [configureSlice] : []}
      />
    </Box>
  )
})

export default MetricAbout
